import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { AudioLines, Download } from 'lucide-react';
import ReviewStage from '../components/ReviewStage';
import BreadcrumbBar from '../ui/BreadcrumbBar';
import Button from '../ui/Button';
import ErrorState from '../ui/ErrorState';
import Icon from '../ui/Icon';
import Skeleton from '../ui/Skeleton';
import Stage from '../ui/Stage';
import Transport from '../ui/Transport';
import { useToast } from '../ui/Toast';
import { useMedia } from '../shell/AppShell';
import { TierToggle } from '../shell/ReviewShell';
import { clean } from '../shell/copy';
import { API_BASE } from '../lib/apiBase';

// Footage share viewer (/share/footage/:token): one Raw footage clip on the review frame (DESIGN rev 2.1 §2.6 Review set,
// §3.3; share-A.html), dark by default with the toggle (ReviewShell, tierKey "footage"). The breadcrumb bar, the stage and
// the transport as /share/:token has them; there is no comments column (a footage link has no comment API), so the stage
// takes the width. Nobody is asked for a name or email: the API takes none.
// What the page can show is bounded by what the API returns: the file name, a playable URL, a still, and whether download
// is allowed. No client name, transcript or match data is in the payload, so none can show. Calls, unchanged:
//   GET /api/share/footage/:token            → { archived, shareLink: { allowDownload }, clip } | { archived: true, fileName }
//   GET /api/share/footage/:token/download   → { url } (when the link allows downloads)
// States: loading, the clip (video or audio), archived (the clip went to cold storage after the link was made), inactive
// (404: turned off or expired), failed to load (anything else: retry).

const CRUMB = 'Shared footage';

// An audio clip has no picture: the stage shows that it is sound, and the same Transport drives an <audio> element.
function AudioStage({ src, label }) {
  const el = useRef(null);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const phone = useMedia('(max-width: 600px)');
  const a = () => el.current;
  const sync = () => {
    const x = a(); if (!x) return;
    setPlaying(!x.paused); setMuted(x.muted); setSpeed(x.playbackRate);
    if (Number.isFinite(x.duration) && x.duration > 0) setDuration(x.duration);
  };
  const seek = t => { const x = a(); if (!x) return; x.currentTime = t; setTime(t); };
  return (
    <div className="ui-review-stage">
      <Stage label={label} transport={(
        <Transport time={time} duration={duration} playing={playing}
          onPlayPause={() => { const x = a(); if (!x) return; if (x.paused) x.play()?.catch?.(() => {}); else x.pause(); }}
          onSeek={seek}
          speed={speed} onSpeed={phone ? undefined : s => { const x = a(); if (x) x.playbackRate = s; }}
          muted={muted} onMute={phone ? undefined : () => { const x = a(); if (x) x.muted = !x.muted; }} />
      )}>
        <div className="ui-review-msg">
          <Icon icon={AudioLines} size={20} />
          <p className="ui-p ui-text-2">Audio clip</p>
        </div>
        <audio ref={el} preload="metadata" onTimeUpdate={e => setTime(e.currentTarget.currentTime)}
          onLoadedMetadata={sync} onDurationChange={sync} onPlay={sync} onPause={sync} onEnded={sync} onVolumeChange={sync} onRateChange={sync}>
          <source src={src} />
        </audio>
      </Stage>
    </div>
  );
}

// A message in the frame's place, under the bar (archived, inactive, failed).
function Message({ crumbs, children }) {
  return (
    <div className="ui ui-review ui-review--fit ui-review--no-comments">
      <BreadcrumbBar crumbs={crumbs} actions={<TierToggle />} />
      <div className="ui-review-msg">{children}</div>
    </div>
  );
}

export default function FootageShareViewer() {
  const { token } = useParams();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inactive, setInactive] = useState(false);
  const [failed, setFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    setFailed(false);
    axios.get(`${API_BASE}/api/share/footage/${token}`)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (cancelled) return; if (err.response?.status === 404) setInactive(true); else setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);
  useEffect(() => load(), [load]);

  async function handleDownload() {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/share/footage/${token}/download`);
      window.open(res.data.url, '_blank');
    } catch (err) {
      toast(clean(err?.response?.data?.error) || 'The download did not start. Try again.', { tone: 'danger' });
    } finally {
      setDownloading(false);
    }
  }

  if (loading) {
    return (
      <div className="ui ui-review ui-review--fit ui-review--no-comments" aria-busy="true">
        <BreadcrumbBar crumbs={[CRUMB]} actions={<TierToggle />} />
        <Stage label="Loading footage"><Skeleton variant="block" label="Loading footage" /></Stage>
      </div>
    );
  }

  if (failed) {
    return (
      <Message crumbs={[CRUMB]}>
        <ErrorState title="The footage did not load" message="Check your connection and try again." onRetry={() => { setLoading(true); load(); }} />
      </Message>
    );
  }

  if (inactive || !data) {
    return (
      <Message crumbs={[CRUMB]}>
        <h1 className="ui-heading">This link is no longer active</h1>
        <p className="ui-p ui-text-2">The share link was turned off or has expired. Ask the person who sent it for a new one.</p>
      </Message>
    );
  }

  // The clip went to cold storage after this link was made: its file is not there, so no player is drawn.
  if (data.archived) {
    return (
      <Message crumbs={[CRUMB, clean(data.fileName)].filter(Boolean)}>
        <h1 className="ui-heading">This clip has been archived</h1>
        <p className="ui-p ui-text-2">It is no longer available at this link. Ask the person who sent it if you still need it.</p>
      </Message>
    );
  }

  const { clip, shareLink } = data;
  const name = clean(clip.fileName);
  return (
    <div className="ui ui-review ui-review--fit ui-review--no-comments">
      <BreadcrumbBar crumbs={[CRUMB, name]}
        actions={(
          <>
            {shareLink.allowDownload && (
              <Button variant="secondary" icon={Download} loading={downloading} onClick={handleDownload} data-testid="footage-download">Download</Button>
            )}
            <TierToggle />
          </>
        )} />
      {clip.mediaKind === 'audio'
        ? <AudioStage src={clip.playbackUrl} label={name} />
        : <ReviewStage src={clip.playbackUrl} poster={clip.thumbnailUrl || undefined} label={name} />}
    </div>
  );
}
